import React, { useMemo } from 'react';
import type { EquipmentIsolationAction, IsolationAction, IsolationActionType, IsolationPlan } from '../types';

interface IsolationPlanSidebarProps {
  plan: IsolationPlan | null;
  actions: IsolationAction[];
  selectedActionId: string | null;
  onSelectAction: (actionId: string) => void;
  isOpen: boolean;
  onToggle: (nextOpen: boolean) => void;
  width?: number;
  reviewedInstrumentIds?: Set<string>;
  reviewConfirmingId?: string | null;
  onMarkAsReviewed?: () => void;
  /** Whether the user holds the review permission; without it the review toggle is disabled. */
  canReview: boolean;
}

const REVIEW_PERMISSION_NEEDED = 'Marking an item as reviewed needs the review permission.';

const panelHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '10px 14px',
  borderBottom: '1px solid #e5e7eb',
  backgroundColor: '#f8fafc',
  flexShrink: 0,
};

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 11,
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  borderBottom: '1px solid #e5e7eb',
  padding: '6px 5px',
  color: '#374151',
  fontWeight: 600,
  position: 'sticky',
  top: 0,
  backgroundColor: '#f9fafb',
  zIndex: 1,
};

const tdStyle: React.CSSProperties = {
  borderBottom: '1px solid #f1f5f9',
  padding: '5px 5px',
  verticalAlign: 'top',
  color: '#111827',
};

function badgeColor(action: IsolationActionType, isReviewed: boolean): React.CSSProperties {
  if (isReviewed) {
    return { color: '#15803d', backgroundColor: '#dcfce7', border: '1px solid #86efac' };
  }
  if (action === 'CLOSE') {
    return { color: '#b91c1c', backgroundColor: '#fee2e2', border: '1px solid #fecaca' };
  }
  if (action === 'OPEN') {
    return { color: '#1d4ed8', backgroundColor: '#dbeafe', border: '1px solid #bfdbfe' };
  }
  return { color: '#92400e', backgroundColor: '#fef3c7', border: '1px solid #fde68a' };
}

function sourceLabel(action: IsolationAction): string {
  switch (action.source) {
    case 'relationship':
      return 'Relationship';
    case 'tag_reference':
      return 'Tag Ref';
    case 'instrument_attachment':
      return 'Attachment';
    default:
      return 'Graph';
  }
}

function StepRow({
  action,
  isSelected,
  isReviewed,
  onClick,
}: {
  action: IsolationAction;
  isSelected: boolean;
  isReviewed: boolean;
  onClick: () => void;
}) {
  return (
    <tr
      onClick={action.highlightable ? onClick : undefined}
      style={{
        cursor: action.highlightable ? 'pointer' : 'default',
        backgroundColor: isSelected ? '#eff6ff' : undefined,
        transition: 'background-color 0.1s',
        opacity: action.highlightable ? 1 : 0.88,
      }}
      onMouseEnter={(e) => {
        if (!isSelected && action.highlightable) (e.currentTarget.style.backgroundColor = '#f8fafc');
      }}
      onMouseLeave={(e) => {
        if (!isSelected && action.highlightable) (e.currentTarget.style.backgroundColor = '');
      }}
    >
      <td style={{ ...tdStyle, fontWeight: 600, textAlign: 'center', width: 32 }}>
        {action.step_number}
      </td>
      <td style={tdStyle}>
        <div style={{ fontWeight: 600 }}>{action.instrument_ref}</div>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
          <span
            style={{
              display: 'inline-flex',
              padding: '1px 5px',
              borderRadius: 999,
              fontSize: 9,
              fontWeight: 600,
              color: '#475569',
              backgroundColor: '#f1f5f9',
              border: '1px solid #cbd5e1',
            }}
          >
            {sourceLabel(action)}
          </span>
          {!action.highlightable && (
            <span
              style={{
                display: 'inline-flex',
                padding: '1px 5px',
                borderRadius: 999,
                fontSize: 9,
                fontWeight: 600,
                color: '#92400e',
                backgroundColor: '#fffbeb',
                border: '1px solid #fcd34d',
              }}
            >
              No canvas location
            </span>
          )}
        </div>
      </td>
      <td style={tdStyle}>
        <span
          style={{
            ...badgeColor(action.action, isReviewed),
            display: 'inline-flex',
            padding: '1px 5px',
            borderRadius: 999,
            fontSize: 10,
            fontWeight: 600,
          }}
        >
          {isReviewed ? 'REVIEWED' : action.action}
        </span>
      </td>
      <td style={tdStyle}>{action.line_no || '-'}</td>
      <td style={{ ...tdStyle, fontSize: 10, color: '#6b7280' }}>
        <div>{action.reasoning}</div>
        {action.evidence_ref && (
          <div style={{ marginTop: 4, color: '#94a3b8' }}>
            Ref: {action.evidence_ref}
          </div>
        )}
      </td>
    </tr>
  );
}

export default function IsolationPlanSidebar({
  plan,
  actions,
  selectedActionId,
  onSelectAction,
  isOpen,
  onToggle,
  width = 420,
  reviewedInstrumentIds = new Set(),
  reviewConfirmingId = null,
  onMarkAsReviewed = () => {},
  canReview,
}: IsolationPlanSidebarProps) {
  const stats = useMemo(() => {
    if (!plan) return null;
    const totalActions = plan.paths.reduce((sum, p) => sum + p.actions.length, 0) + plan.equipment_actions.length;
    const isolated = plan.paths.filter((p) => p.has_isolation).length;
    const noBoundary = plan.paths.filter((p) => !p.has_isolation).length;
    const equipmentLoto = plan.equipment_isolations?.length ?? 0;
    return { totalPaths: plan.paths.length, totalActions, isolated, noBoundary, equipmentLoto };
  }, [plan]);

  return (
    <>
      <button
        onClick={() => onToggle(!isOpen)}
        style={{
          position: 'absolute',
          top: 12,
          right: isOpen ? width + 8 : 8,
          zIndex: 25,
          border: '1px solid #d1d5db',
          backgroundColor: '#ffffff',
          borderRadius: 6,
          cursor: 'pointer',
          padding: '5px 10px',
          fontSize: 12,
          color: '#111827',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.08)',
          transition: 'right 0.2s ease',
        }}
      >
        {isOpen ? 'Hide Plan' : 'Show Plan'}
      </button>

      <div
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width,
          backgroundColor: '#ffffff',
          borderLeft: '1px solid #e5e7eb',
          boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.08)',
          transform: isOpen ? 'translateX(0)' : `translateX(${width}px)`,
          transition: 'transform 0.2s ease',
          zIndex: 20,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div style={panelHeaderStyle}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>Isolation Plan</div>
            <div style={{ fontSize: 11, color: '#6b7280' }}>
              {plan ? `${plan.pnid_no} / ${plan.equipment_no}` : 'No plan generated'}
            </div>
          </div>
          <button
            onClick={() => onToggle(false)}
            style={{
              border: '1px solid #d1d5db',
              backgroundColor: '#ffffff',
              borderRadius: 6,
              cursor: 'pointer',
              width: 28,
              height: 28,
              fontSize: 16,
              lineHeight: '26px',
            }}
          >
            &times;
          </button>
        </div>

        {plan && stats && (
          <div
            style={{
              display: 'flex',
              gap: 8,
              padding: '8px 14px',
              borderBottom: '1px solid #e5e7eb',
              backgroundColor: '#f9fafb',
              flexShrink: 0,
              fontSize: 11,
              color: '#374151',
            }}
          >
            <span><strong>{stats.totalPaths}</strong> paths</span>
            <span style={{ color: '#d1d5db' }}>|</span>
            <span><strong>{stats.totalActions}</strong> actions</span>
            <span style={{ color: '#d1d5db' }}>|</span>
            <span style={{ color: '#166534' }}>{stats.isolated} isolated</span>
            {stats.noBoundary > 0 && (
              <>
                <span style={{ color: '#d1d5db' }}>|</span>
                <span style={{ color: '#991b1b' }}>{stats.noBoundary} open</span>
              </>
            )}
            {stats.equipmentLoto > 0 && (
              <>
                <span style={{ color: '#d1d5db' }}>|</span>
                <span style={{ color: '#c2410c' }}>{stats.equipmentLoto} LOTO</span>
              </>
            )}
          </div>
        )}

        {plan && selectedActionId && (
          <div
            style={{
              padding: '8px 14px',
              borderBottom: '1px solid #e5e7eb',
              backgroundColor: reviewedInstrumentIds.has(selectedActionId) ? '#f0fdf4' : '#f0f9ff',
              flexShrink: 0,
            }}
          >
            {reviewedInstrumentIds.has(selectedActionId) ? (
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#15803d',
                  padding: '6px 10px',
                  backgroundColor: '#dcfce7',
                  borderRadius: 6,
                  border: '1px solid #86efac',
                  textAlign: 'center',
                  marginBottom: '8px',
                }}
              >
                ✓ This item is marked as reviewed
              </div>
            ) : null}
            {/* The wrapper repeats the title because not every browser shows a disabled button's own. */}
            <div title={canReview ? undefined : REVIEW_PERMISSION_NEEDED}>
            <button
              onClick={onMarkAsReviewed}
              disabled={!canReview}
              title={canReview ? undefined : REVIEW_PERMISSION_NEEDED}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: 6,
                border: !canReview
                  ? '1px solid var(--raven-divider)'
                  : reviewConfirmingId === selectedActionId ? '2px solid #ea580c' : '1px solid #22c55e',
                backgroundColor: reviewConfirmingId === selectedActionId ? '#fed7aa' : '#ffffff',
                color: !canReview
                  ? 'var(--raven-text-disabled)'
                  : reviewConfirmingId === selectedActionId ? '#92400e' : '#15803d',
                fontSize: 13,
                fontWeight: 600,
                cursor: canReview ? 'pointer' : 'not-allowed',
                transition: 'all 0.2s',
              }}
            >
              {reviewConfirmingId === selectedActionId 
                ? '⚠️ Click again to confirm' 
                : reviewedInstrumentIds.has(selectedActionId)
                ? '📋 Unmark as Reviewed?'
                : '✓ Mark as Reviewed'}
            </button>
            </div>
            {reviewConfirmingId === selectedActionId && (
              <div
                style={{
                  fontSize: 11,
                  color: '#92400e',
                  marginTop: '6px',
                  padding: '6px 10px',
                  backgroundColor: '#fffbeb',
                  borderRadius: 4,
                  textAlign: 'center',
                }}
              >
                Waiting for confirmation (3 seconds)
              </div>
            )}
          </div>
        )}

        <div style={{ flex: 1, overflow: 'auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {!plan && (
            <div style={{ fontSize: 13, color: '#6b7280', padding: 14 }}>
              Select a P&amp;ID and equipment, then generate a plan.
            </div>
          )}

          {plan && plan.warnings.length > 0 && (
            <details style={{ border: '1px solid #fcd34d', margin: '8px 10px 0', borderRadius: 6, overflow: 'hidden', flexShrink: 0 }}>
              <summary
                style={{
                  cursor: 'pointer',
                  listStyle: 'none',
                  padding: '6px 10px',
                  backgroundColor: '#fffbeb',
                  color: '#92400e',
                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                {plan.warnings.length} warning{plan.warnings.length > 1 ? 's' : ''}
              </summary>
              <div style={{ padding: '6px 10px', backgroundColor: '#fffbeb', color: '#92400e', fontSize: 11, borderTop: '1px solid #fcd34d' }}>
                {plan.warnings.map((warning) => (
                  <div key={warning} style={{ paddingBottom: 3 }}>&bull; {warning}</div>
                ))}
              </div>
            </details>
          )}

          {plan && actions.length > 0 && (
            <div style={{ flex: 1, overflow: 'auto', minHeight: 0, padding: '6px 10px' }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={{ ...thStyle, textAlign: 'center', width: 32 }}>Step</th>
                    <th style={thStyle}>Instrument</th>
                    <th style={thStyle}>Action</th>
                    <th style={thStyle}>Line</th>
                    <th style={thStyle}>Reasoning</th>
                  </tr>
                </thead>
                <tbody>
                  {actions.map((action) => (
                    <StepRow
                      key={action.instrument_id}
                      action={action}
                      isSelected={selectedActionId === action.instrument_id}
                      isReviewed={reviewedInstrumentIds.has(action.instrument_id)}
                      onClick={() => onSelectAction(action.instrument_id)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {plan && actions.length === 0 && (
            <div style={{ fontSize: 12, color: '#6b7280', padding: 14, textAlign: 'center' }}>
              No isolation actions in this plan.
            </div>
          )}

          {plan && plan.equipment_isolations && plan.equipment_isolations.length > 0 && (
            <div style={{ padding: '6px 10px', flexShrink: 0 }}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: '#111827',
                  padding: '8px 0 4px',
                  borderTop: '1px solid #e5e7eb',
                }}
              >
                Equipment Isolation (LOTO)
              </div>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Equipment</th>
                    <th style={thStyle}>Type</th>
                    <th style={thStyle}>Action</th>
                    <th style={thStyle}>Reasoning</th>
                  </tr>
                </thead>
                <tbody>
                  {plan.equipment_isolations.map((eq: EquipmentIsolationAction) => (
                    <tr key={eq.equipment_no}>
                      <td style={tdStyle}>
                        <span style={{ fontWeight: 600 }}>{eq.equipment_no}</span>
                        {eq.is_driver && eq.driven_equipment_no && (
                          <div style={{ fontSize: 10, color: '#6b7280' }}>
                            Driver for {eq.driven_equipment_no}
                          </div>
                        )}
                      </td>
                      <td style={tdStyle}>{eq.equipment_type || '-'}</td>
                      <td style={tdStyle}>
                        <span
                          style={{
                            display: 'inline-flex',
                            padding: '1px 5px',
                            borderRadius: 999,
                            fontSize: 10,
                            fontWeight: 600,
                            color: '#c2410c',
                            backgroundColor: '#fff7ed',
                            border: '1px solid #fed7aa',
                          }}
                        >
                          {eq.action}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, fontSize: 10, color: '#6b7280' }}>{eq.reasoning}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
